function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { ArraySet } from "./ArraySet";
import { HistoryBuffer } from "./HistoryBuffer";
import { maybeCaptureParent } from "./capture";
import { EMPTY_ARRAY, equals } from "./helpers";
import { advanceGlobalEpoch, atomDidChange, globalEpoch } from "./transactions";
import { RESET_VALUE } from "./types";
import { logDotValueWarning } from "./warnings";
/**
 * WARNING!
 *
 * I made a change to the initialization so that lastChangedEpoch
 * gets set in the constructor rather than in the class itself.
 *
 * This was required to fix some of the tests and _should_ have the
 * equivalent behavior.
 *
 */ /**
 * @internal
 */ export class _Atom {
    __unsafe__getWithoutCapture() {
        return this.current;
    }
    get() {
        maybeCaptureParent(this);
        return this.current;
    }
    /**
   * @deprecated Use [[Atom.get]] instead.
   */ // eslint-disable-next-line no-restricted-syntax
    get value() {
        logDotValueWarning();
        return this.get();
    }
    set(value, diff) {
        var _this_isEqual, _this;
        var _this_isEqual1;
        // If the value has not changed, do nothing.
        if ((_this_isEqual1 = (_this_isEqual = (_this = this).isEqual) === null || _this_isEqual === void 0 ? void 0 : _this_isEqual.call(_this, this.current, value)) !== null && _this_isEqual1 !== void 0 ? _this_isEqual1 : equals(this.current, value)) {
            return this.current;
        }
        // Tick forward the global epoch
        advanceGlobalEpoch();
        // Add the diff to the history buffer.
        if (this.historyBuffer) {
            var _this_computeDiff, _this1;
            var _ref;
            this.historyBuffer.pushEntry(this.lastChangedEpoch, globalEpoch, (_ref = diff !== null && diff !== void 0 ? diff : (_this_computeDiff = (_this1 = this).computeDiff) === null || _this_computeDiff === void 0 ? void 0 : _this_computeDiff.call(_this1, this.current, value, this.lastChangedEpoch, globalEpoch)) !== null && _ref !== void 0 ? _ref : RESET_VALUE);
        }
        // Update the atom's record of the epoch when last changed.
        this.lastChangedEpoch = globalEpoch;
        const oldValue = this.current;
        this.current = value;
        // Notify all children that this atom has changed.
        atomDidChange(this, oldValue);
        return value;
    }
    update(updater) {
        return this.set(updater(this.current));
    }
    getDiffSince(epoch) {
        var _this_historyBuffer;
        maybeCaptureParent(this);
        // If no changes have occurred since the given epoch, return an empty array.
        if (epoch >= this.lastChangedEpoch) {
            return EMPTY_ARRAY;
        }
        var _this_historyBuffer_getChangesSince;
        return (_this_historyBuffer_getChangesSince = (_this_historyBuffer = this.historyBuffer) === null || _this_historyBuffer === void 0 ? void 0 : _this_historyBuffer.getChangesSince(epoch)) !== null && _this_historyBuffer_getChangesSince !== void 0 ? _this_historyBuffer_getChangesSince : RESET_VALUE;
    }
    constructor(name, current, options){
        _define_property(this, "name", void 0);
        _define_property(this, "current", void 0);
        _define_property(this, "isEqual", void 0);
        _define_property(this, "computeDiff", void 0);
        _define_property(this, "lastChangedEpoch", void 0);
        _define_property(this, "children", void 0);
        _define_property(this, "historyBuffer", void 0);
        this.name = name;
        this.current = current;
        this.children = new ArraySet();
        var _options_isEqual;
        this.isEqual = (_options_isEqual = options === null || options === void 0 ? void 0 : options.isEqual) !== null && _options_isEqual !== void 0 ? _options_isEqual : null;
        this.lastChangedEpoch = globalEpoch;
        if (!options) return;
        if (options.historyLength) {
            this.historyBuffer = new HistoryBuffer(options.historyLength);
        }
        this.computeDiff = options.computeDiff;
    }
}
/**
 * Creates a new [[Atom]].
 *
 * An Atom is a signal that can be updated directly by calling [[Atom.set]] or [[Atom.update]].
 *
 * @example
 * ```ts
 * const name = atom('name', 'John')
 *
 * name.get() // 'John'
 *
 * name.set('Jane')
 *
 * name.get() // 'Jane'
 * ```
 *
 * @public
 */ export function atom(/**
   * A name for the signal. This is used for debugging and profiling purposes, it does not need to be unique.
   */ name, /**
   * The initial value of the signal.
   */ initialValue, /**
   * The options to configure the atom. See [[AtomOptions]].
   */ options) {
    return new _Atom(name, initialValue, options);
}
/**
 * Returns true if the given value is an [[Atom]].
 * @public
 */ export function isAtom(value) {
    return value instanceof _Atom;
}
